import { useEffect, useRef, useState } from "react";

import {
  getTeleconsultationRTMToken,
  joinPresence,
  leavePresence,
  sendPresenceHeartbeat,
} from "@utils/rtmApi";
import { rtmService } from "@utils/rtmService";

import { ROLES } from "@constants/index";

import { useAuth } from "@contexts/auth";


const HEARTBEAT_INTERVAL_MS = 10000;

interface UseRTMPresenceProps {
  consultationId: string;
  enabled?: boolean;
}

export const useRTMPresence = ({
  consultationId,
  enabled = true,
}: UseRTMPresenceProps) => {
  const { id, fullName, roles } = useAuth();
  const [error, setError] = useState<Error | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const sessionRef = useRef<{ channelId: string; userId: string } | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let cancelled = false;

    const stopHeartbeat = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };

    const cleanup = async () => {
      stopHeartbeat();
      const session = sessionRef.current;
      sessionRef.current = null;

      if (session) {
        try {
          await leavePresence(session);
        } catch (leaveError) {
          import.meta.env.DEV &&
            console.error("[RTM Presence] Leave failed:", leaveError);
        }
      }

      try {
        await rtmService.cleanup();
      } catch (cleanupError) {
        import.meta.env.DEV &&
          console.error("[RTM Presence] Cleanup failed:", cleanupError);
      }

      if (!cancelled) {
        setIsJoined(false);
      }
    };

    const start = async () => {
      if (!enabled || !consultationId || !id) {
        await cleanup();
        return;
      }

      try {
        setError(null);

        const tokenData = await getTeleconsultationRTMToken(consultationId);
        const channelId = tokenData.channel_id;

        if (!channelId) {
          throw new Error("RTM token response is missing channel_id");
        }

        await rtmService.initialize({
          appId: tokenData.app_id,
          userId: tokenData.user_id,
          token: tokenData.token,
        });
        await rtmService.joinChannel(channelId);
        await joinPresence({
          channelId,
          userId: tokenData.user_id,
          metadata: {
            role: roles.includes(ROLES.DOCTOR)
              ? ROLES.DOCTOR
              : roles[0] ?? "staff",
            roles,
            name: fullName,
          },
        });

        if (cancelled) {
          await cleanup();
          return;
        }

        sessionRef.current = {
          channelId,
          userId: tokenData.user_id,
        };
        setIsJoined(true);

        heartbeatRef.current = setInterval(() => {
          const session = sessionRef.current;
          if (!session) return;

          sendPresenceHeartbeat(session).catch(heartbeatError => {
            import.meta.env.DEV &&
              console.error("[RTM Presence] Heartbeat failed:", heartbeatError);
          });
        }, HEARTBEAT_INTERVAL_MS);
      } catch (err) {
        const nextError = err instanceof Error ? err : new Error(String(err));
        setError(nextError);
        setIsJoined(false);
        import.meta.env.DEV &&
          console.error("[RTM Presence] Join failed:", nextError);
      }
    };

    start();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [consultationId, enabled, fullName, id, roles]);

  return {
    error,
    isJoined,
  };
};
